import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { Button, Card, CardBody, CardHeader } from "reactstrap";
import api from "services/api";
import { useAuth } from "hooks/auth";
import { useFeatures } from "hooks/features";
import {
  PERMISSAO_VER,
  temPermissao,
  urlAuditoriaPedido,
} from "views/Empresa/AuditoriaPedido/regras";
import { formatCurrency } from "../../../../utils/formatCurrency";
import { getQuoteStatusLabel } from "../statusOptions";
import {
  autopilotBodyStyle,
  autopilotCardStyle,
  autopilotDescriptionStyle,
  autopilotEyebrowStyle,
  autopilotHeaderStyle,
  autopilotInsetCardStyle,
  autopilotSecondaryButtonStyle,
  autopilotTitleStyle,
} from "../../../../components/autopilot/styles";

const STATUS_RULES = {
  "Pendente": {
    color: "var(--everlog-status-neutral)",
    bg: "color-mix(in srgb, var(--everlog-status-neutral) 10%, transparent)",
    text: "Edição livre — alterações salvas sem criar nova versão.",
  },
  "Stand-by": {
    color: "var(--everlog-status-warning)",
    bg: "color-mix(in srgb, var(--everlog-status-warning) 10%, transparent)",
    text: "Edição permitida — cada alteração gera um histórico rastreável.",
  },
  "Simulado": {
    color: "var(--everlog-status-info)",
    bg: "color-mix(in srgb, var(--everlog-status-info) 10%, transparent)",
    text: "Cotação bloqueada para edição. Crie uma nova versão para alterar dados.",
  },
  "Realizado": {
    color: "var(--everlog-status-success)",
    bg: "color-mix(in srgb, var(--everlog-status-success) 10%, transparent)",
    text: "Cotação finalizada. Nenhuma alteração é permitida.",
  },
};

const STATUS_CLASS = {
  Pendente: "lc-badge-pendente",
  "Stand-by": "lc-badge-standy",
  Simulado: "lc-badge-simulado",
  Realizado: "lc-badge-realizado",
};

const panelShellStyle = {
  ...autopilotCardStyle,
  display: "flex",
  flexDirection: "column",
  background: "var(--everlog-card-hero-bg)",
};

const StatusRulesBanner = ({ status }) => {
  const rule = STATUS_RULES[status];
  if (!rule) return null;
  return (
    <div
      className="lc-status-banner"
      style={{
        "--lc-status-bg": rule.bg,
        "--lc-status-color": rule.color,
      }}
    >
      <strong>{getQuoteStatusLabel(status)}</strong> — {rule.text}
    </div>
  );
};

const Row = ({ label, value, cls }) => (
  <div className="lc-fin-row">
    <span className="lc-fin-label">{label}</span>
    <span className={`lc-fin-value ${cls || ""}`}>{value}</span>
  </div>
);

const PanelSection = ({ title, children }) => (
  <div className="lc-panel-inset" style={autopilotInsetCardStyle}>
    <div className="lc-panel-inset__header">
      <div className="lc-panel-section-title">{title}</div>
    </div>
    <div className="lc-panel-inset__body">{children}</div>
  </div>
);

// PO1-2863: cotado × consumido vem de GET /embarcadores/embarques/:id/consumo
// (CT-e × NF cruzados por NotaFiscal.Num_Pedido = BR ou 'EVLOG_<nº>').
// Erro de rede/servidor é tratado como "sem consumo" — o painel nunca quebra.
const CONSUMO_VAZIO = { status: "idle", data: null };

const useConsumo = (embarqueId) => {
  const [consumo, setConsumo] = useState(CONSUMO_VAZIO);

  useEffect(() => {
    if (!embarqueId) {
      setConsumo(CONSUMO_VAZIO);
      return undefined;
    }
    let ativo = true;
    setConsumo({ status: "loading", data: null });
    api
      .get(`/embarcadores/embarques/${embarqueId}/consumo`)
      .then(({ data }) => {
        if (ativo) setConsumo({ status: "ok", data: data || null });
      })
      .catch((err) => {
        console.warn("[FinancialPanel] consumo indisponível:", err && err.message);
        if (ativo) setConsumo({ status: "error", data: null });
      });
    return () => {
      ativo = false;
    };
  }, [embarqueId]);

  return consumo;
};

const FinancialPanel = ({ emb, onOpen }) => {
  const consumo = useConsumo(emb ? emb._id : null);
  const history = useHistory();
  const { user } = useAuth();
  const { codes } = useFeatures();
  // PO1-3240: a Auditoria do Pedido fica no Flow; sem o produto ou sem a
  // permissão da Auditoria de Faturas o botão nem aparece.
  const podeAuditar = Boolean(codes && codes.has("FLOW")) && temPermissao(user, PERMISSAO_VER);

  if (!emb) {
    return (
      <Card className="card-everlog-shell lc-panel" style={panelShellStyle}>
        <CardHeader className="lc-panel__header" style={autopilotHeaderStyle}>
          <div style={autopilotEyebrowStyle}>Detalhe lateral</div>
          <div style={autopilotTitleStyle}>Consolidação financeira</div>
          <p style={autopilotDescriptionStyle}>
            Selecione uma cotação na lista para abrir o resumo financeiro e a regra de
            edição.
          </p>
        </CardHeader>
        <CardBody className="lc-panel__body" style={autopilotBodyStyle}>
          <div className="lc-panel__empty" style={autopilotInsetCardStyle}>
            <span className="lc-panel__empty-icon">◫</span>
            <div className="lc-empty-title">Nenhuma cotação selecionada</div>
            <p>
              Escolha um item da lista para visualizar a consolidação de frete,
              mercadoria e restrições de edição.
            </p>
          </div>
        </CardBody>
      </Card>
    );
  }

  // PO1-3771: o consumo é do BR inteiro (todos os CT-e/NF do pedido). Quando o
  // BR tem mais de uma cotação aprovada (PO1-3272), o cotado e a mercadoria
  // também vêm somados do BR (mesma regra da Auditoria do Pedido); com uma só,
  // fica tudo como antes, pelos valores da cotação aberta.
  const consumoOk = consumo.status === "ok" && consumo.data;
  const qtdCotacoesBR = consumoOk ? Number(consumo.data.qtdCotacoesBR) || 0 : 0;
  const somaDoBR = qtdCotacoesBR > 1;
  const valorTotalCotado = somaDoBR
    ? Number(consumo.data.cotadoBR) || 0
    : emb.valor_total || emb.valorFrete || 0;
  const encontrado = consumo.status === "ok" && consumo.data && consumo.data.encontrado;
  const valorConsumidoFrete = encontrado ? Number(consumo.data.valorFrete) || 0 : null;
  const saldoFrete = valorConsumidoFrete != null
    ? valorTotalCotado - valorConsumidoFrete
    : null;

  const valorMercadoria = somaDoBR ? Number(consumo.data.valorNFBR) || 0 : emb.valorNF || 0;
  const valorConsumidoMerc = encontrado ? Number(consumo.data.valorNotaFiscal) || 0 : null;
  const saldoMerc = valorConsumidoMerc != null
    ? valorMercadoria - valorConsumidoMerc
    : null;

  // Texto do consumo: carregando / vinculado (qtd NF e CT-e) / nada vinculado.
  let consumoNota;
  if (consumo.status === "loading") {
    consumoNota = "Consultando NF e CT-e vinculados…";
  } else if (encontrado) {
    const { qtdNf, qtdCte } = consumo.data;
    consumoNota = `${qtdNf} NF · ${qtdCte} CT-e vinculados`;
  } else {
    consumoNota = "Nenhuma NF/CT-e vinculada";
  }
  const consumoPlaceholder = consumo.status === "loading" ? "…" : "—";

  const cidade = [emb.cidadeDestino, emb.ufDestino].filter(Boolean).join(" / ");
  const statusClass = STATUS_CLASS[emb.statusEmbarque] || "lc-badge-pendente";
  const summaryLine = [emb.cliente, cidade].filter(Boolean).join(" · ");

  return (
    <Card className="card-everlog-shell lc-panel" style={panelShellStyle}>
      <CardHeader className="lc-panel__header" style={autopilotHeaderStyle}>
        <div style={autopilotEyebrowStyle}>Detalhe lateral</div>
        <h3 className="lc-panel__title">Cotação {emb.numEmbarque || "—"}</h3>
        <p className="lc-panel__description">
          {summaryLine || "Sem identificação complementar para esta cotação."}
        </p>
        <div className="lc-panel__meta">
          {emb.numeroCotacao != null && (
            <span className="lc-badge lc-badge-cotacao" title="Nº da cotação">
              Nº {emb.numeroCotacao}
            </span>
          )}
          <span className={`lc-badge ${statusClass}`}>
            {getQuoteStatusLabel(emb.statusEmbarque)}
          </span>
          {emb.transportadora ? (
            <span className="lc-panel__meta-pill">{emb.transportadora}</span>
          ) : null}
        </div>
      </CardHeader>

      <CardBody className="lc-panel__body" style={autopilotBodyStyle}>
        <div className="lc-panel-stack">
          <div className="lc-panel-inset" style={autopilotInsetCardStyle}>
            <div className="lc-panel-inset__body">
              <StatusRulesBanner status={emb.statusEmbarque} />
            </div>
          </div>

          {somaDoBR && (
            <div className="lc-br-note" data-testid="lc-br-note">
              Este pedido ({consumo.data.codigoPedido}) tem {qtdCotacoesBR} cotações
              aprovadas; valores somados.
              {consumo.data.incluiEstaCotacao === false
                ? " Esta cotação não entra na soma."
                : null}
              {podeAuditar && (
                <>
                  {" "}
                  <button
                    type="button"
                    className="lc-link-button"
                    onClick={() => history.push(urlAuditoriaPedido(consumo.data.codigoPedido))}
                  >
                    Ver na Auditoria do Pedido
                  </button>
                </>
              )}
            </div>
          )}

          <PanelSection title="Frete">
            <Row
              label="Valor Total Cotado"
              value={formatCurrency(valorTotalCotado)}
            />
            <Row
              label="Valor Total Consumido"
              value={valorConsumidoFrete != null ? formatCurrency(valorConsumidoFrete) : consumoPlaceholder}
              cls={valorConsumidoFrete != null ? "" : "muted"}
            />
            <Row
              label="Saldo de Frete"
              value={saldoFrete != null ? formatCurrency(saldoFrete) : consumoPlaceholder}
              cls={saldoFrete != null ? (saldoFrete >= 0 ? "green" : "negative") : "muted"}
            />
            <Row label="Vínculo" value={consumoNota} cls={encontrado ? "" : "muted"} />
          </PanelSection>

          <PanelSection title="Mercadoria">
            <Row
              label="Valor Total Mercadoria"
              value={formatCurrency(valorMercadoria)}
            />
            <Row
              label="Valor Consumido Mercadoria"
              value={valorConsumidoMerc != null ? formatCurrency(valorConsumidoMerc) : consumoPlaceholder}
              cls={valorConsumidoMerc != null ? "" : "muted"}
            />
            <Row
              label="Saldo de Mercadoria"
              value={saldoMerc != null ? formatCurrency(saldoMerc) : consumoPlaceholder}
              cls={saldoMerc != null ? (saldoMerc >= 0 ? "green" : "negative") : "muted"}
            />
          </PanelSection>
        </div>

        <div className="lc-panel__actions">
          <Button
            className="btn-sm"
            style={autopilotSecondaryButtonStyle}
            block
            onClick={() => onOpen(emb)}
            disabled={emb.statusEmbarque === "Realizado"}
            title={
              emb.statusEmbarque === "Realizado"
                ? "Cotação finalizada — nenhuma alteração permitida"
                : undefined
            }
          >
            {emb.statusEmbarque === "Realizado" ? "🔒 Cotação finalizada" : "Editar cotação"}
          </Button>
          {podeAuditar && (
            <Button
              className="btn-sm"
              color="primary"
              block
              disabled={!(encontrado && consumo.data.qtdCte > 0)}
              title={encontrado && consumo.data.qtdCte > 0 ? undefined : "Ainda sem CT-e para este pedido"}
              onClick={() => history.push(urlAuditoriaPedido(consumo.data.chaveConsulta))}
            >
              Auditar frete
            </Button>
          )}
        </div>
      </CardBody>
    </Card>
  );
};

export default FinancialPanel;
